import { useEffect, useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, CircleDollarSign, FileText, History, Stethoscope, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { usePacientes } from "@/hooks/usePacientes";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { apiList } from "@/lib/api";
import { parseLocalDateTime } from "@/lib/date-utils";

type TimelineItem = {
  id: string;
  date: string;
  title: string;
  description: string;
  type: "consulta" | "evolucao" | "plano" | "documento" | "servico";
  status?: string;
  value?: number;
};

type AppointmentRecord = { id: string; paciente_id?: string; data_agendamento?: string; created_at?: string; procedimento?: string; tipo_consulta?: string; observacoes?: string; status?: string; valor?: number };
type EvolutionRecord = { id: string; paciente_id?: string; data?: string; created_at?: string; procedimento?: string; evolucao?: string; conduta?: string };
type PlanRecord = { id: string; paciente_id?: string; updated_at?: string; created_at?: string; titulo?: string; itens?: unknown[]; status?: string };
type DocumentRecord = { id: string; paciente_id?: string; data_emissao?: string; created_at?: string; titulo?: string; tipo?: string; descricao?: string; valor?: number };
type ServiceRecord = { id: string; paciente_id?: string; data_conclusao?: string; updated_at?: string; data_abertura?: string; created_at?: string; numero_os?: string; procedimentos?: Array<{ descricao?: string }>; status?: string; valor_total?: number };

const money = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const normalizeStatus = (status?: string) => String(status || "").trim().toLocaleLowerCase("pt-BR");
const completedStatus = (status?: string) => ["realizado", "realizada", "concluido", "concluído", "concluida", "concluída", "finalizado", "finalizada", "pago", "paga"].includes(normalizeStatus(status));
const missedStatus = (status?: string) => ["faltou", "falta", "não compareceu", "nao compareceu"].includes(normalizeStatus(status));

const eventDate = (value: string) => {
  const date = parseLocalDateTime(value);
  return Number.isNaN(date.getTime()) ? "Data não informada" : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

export function PatientTimeline() {
  const { pacientes } = usePacientes();
  const activeClinicaId = useActiveClinicaId();
  const [pacienteId, setPacienteId] = useState("");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<TimelineItem[]>([]);

  useEffect(() => {
    if (pacienteId && !pacientes.some((patient) => patient.id === pacienteId)) setPacienteId("");
  }, [pacienteId, pacientes]);

  useEffect(() => {
    if (!pacienteId) {
      setEvents([]);
      return;
    }
    let active = true;
    setLoading(true);
    Promise.all([
      apiList<AppointmentRecord>("agendamentos"),
      apiList<EvolutionRecord>("evolucoes_clinicas"),
      apiList<PlanRecord>("planos_tratamento"),
      apiList<DocumentRecord>("documentos_emitidos"),
      apiList<ServiceRecord>("ordem_servico"),
    ]).then(([appointments, evolutions, plans, documents, services]) => {
      if (!active) return;
      const result: TimelineItem[] = [];
      appointments.filter((item) => item.paciente_id === pacienteId).forEach((item) => result.push({
        id: `appointment-${item.id}`,
        date: item.data_agendamento || item.created_at || "",
        title: item.procedimento || item.tipo_consulta || "Consulta odontológica",
        description: item.observacoes || "Agendamento registrado na agenda.",
        type: "consulta",
        status: item.status,
        value: Number(item.valor || 0),
      }));
      evolutions.filter((item) => item.paciente_id === pacienteId).forEach((item) => result.push({
        id: `evolution-${item.id}`,
        date: item.data || item.created_at || "",
        title: item.procedimento || "Evolução clínica",
        description: item.evolucao || item.conduta || "Evolução registrada no prontuário.",
        type: "evolucao",
      }));
      plans.filter((item) => item.paciente_id === pacienteId).forEach((item) => result.push({
        id: `plan-${item.id}`,
        date: item.updated_at || item.created_at || "",
        title: item.titulo || "Plano de tratamento",
        description: `${(item.itens || []).length} procedimento(s) no plano.`,
        type: "plano",
        status: item.status,
      }));
      documents.filter((item) => item.paciente_id === pacienteId).forEach((item) => result.push({
        id: `document-${item.id}`,
        date: item.data_emissao || item.created_at || "",
        title: item.titulo || item.tipo || "Documento emitido",
        description: item.descricao || "Documento vinculado ao paciente.",
        type: "documento",
        value: Number(item.valor || 0),
      }));
      services.filter((item) => item.paciente_id === pacienteId).forEach((item) => result.push({
        id: `service-${item.id}`,
        date: item.data_conclusao || item.updated_at || item.data_abertura || item.created_at || "",
        title: item.numero_os ? `Ordem de serviço ${item.numero_os}` : "Ordem de serviço",
        description: (item.procedimentos || []).map((procedure) => procedure.descricao).filter(Boolean).join(", ") || "Serviço registrado.",
        type: "servico",
        status: item.status,
        value: Number(item.valor_total || 0),
      }));
      setEvents(result.sort((a, b) => String(b.date).localeCompare(String(a.date))));
    }).catch(() => active && setEvents([])).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [activeClinicaId, pacienteId]);

  const summary = useMemo(() => {
    const appointments = events.filter((item) => item.type === "consulta");
    const services = events.filter((item) => item.type === "servico");
    return {
      visits: appointments.filter((item) => completedStatus(item.status)).length,
      missed: appointments.filter((item) => missedStatus(item.status)).length,
      production: [...appointments.filter((item) => completedStatus(item.status)), ...services.filter((item) => completedStatus(item.status))]
        .reduce((sum, item) => sum + Number(item.value || 0), 0),
    };
  }, [events]);

  const iconFor = (type: TimelineItem["type"]) => {
    if (type === "consulta") return CalendarClock;
    if (type === "evolucao") return Stethoscope;
    return FileText;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><History className="h-5 w-5" /> Histórico consolidado</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Select value={pacienteId} onValueChange={setPacienteId}>
            <SelectTrigger className="max-w-xl"><SelectValue placeholder="Selecione o paciente" /></SelectTrigger>
            <SelectContent>{pacientes.map((patient) => <SelectItem key={patient.id} value={patient.id}>{patient.nome}</SelectItem>)}</SelectContent>
          </Select>
          {pacienteId && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-md border p-3"><CheckCircle2 className="mb-2 h-4 w-4 text-emerald-600" /><p className="text-xl font-semibold">{summary.visits}</p><p className="text-xs text-muted-foreground">atendimentos concluídos</p></div>
              <div className="rounded-md border p-3"><XCircle className="mb-2 h-4 w-4 text-destructive" /><p className="text-xl font-semibold">{summary.missed}</p><p className="text-xs text-muted-foreground">faltas registradas</p></div>
              <div className="rounded-md border p-3"><CircleDollarSign className="mb-2 h-4 w-4 text-primary" /><p className="text-xl font-semibold">{money(summary.production)}</p><p className="text-xs text-muted-foreground">produção concluída registrada</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div> : pacienteId && events.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Nenhum evento encontrado para este paciente nesta unidade.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {events.map((item) => {
            const Icon = iconFor(item.type);
            return <Card key={item.id}><CardContent className="flex gap-3 p-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2"><strong>{item.title}</strong><span className="text-xs text-muted-foreground">{eventDate(item.date)}</span></div>
                <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                <div className="mt-2 flex flex-wrap gap-2">{item.status && <Badge variant="secondary">{item.status}</Badge>}{Number(item.value || 0) > 0 && <Badge variant="outline">{money(Number(item.value))}</Badge>}</div>
              </div>
            </CardContent></Card>;
          })}
        </div>
      )}
    </div>
  );
}
